import { Flex, Text } from '@buffer-mono/popcorn'

import { EngagementAvatar } from '../EngagementAvatar'
import { EngagementItem } from '../EngagementItem'
import type { EngagementReplyFormChannel } from '../EngagementReplyForm/hooks/types'

import type { JSX } from 'react'

type EngagementReplyProcessingProps = {
  channel: EngagementReplyFormChannel
  text: string
  showConnector?: boolean
  /**
   * Test id forwarded to the underlying row. Defaults to the generic
   * `engagement-item`; the per-feature reply sections pass their own
   * (`comment-item` / `mention-item`) to match existing assertions.
   */
  'data-testid'?: string
}

/**
 * "Sending…" indicator shown in place of a reply while the mutation is in
 * flight. Once it succeeds the real (optimistic) reply takes its place via the
 * per-feature last-replies list. Sent as the channel, so it wears the channel
 * avatar. Shared by comment and mention reply sections.
 */
export const EngagementReplyProcessing = ({
  channel,
  text,
  showConnector = false,
  'data-testid': dataTestId = 'engagement-item',
}: EngagementReplyProcessingProps): JSX.Element => {
  return (
    <EngagementItem
      as="div"
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- This shared reply row is the live status region while preserving the EngagementItem layout.
      role="status"
      aria-label="Reply is sending"
      aria-live="polite"
      data-testid={dataTestId}
      variant="lastReply"
      avatar={
        <EngagementAvatar
          hasParent={true}
          avatar={channel.avatar}
          username={channel.name}
          showConnector={showConnector}
        />
      }
    >
      <Flex direction="column" gap="2xs">
        <header>
          <Flex align="center" gap="xs">
            <Text weight="bold">{channel.name}</Text>
            <Text size="sm" color="subtle">
              Sending...
            </Text>
          </Flex>
        </header>
        <Text color="default">{text}</Text>
      </Flex>
    </EngagementItem>
  )
}

EngagementReplyProcessing.displayName = 'EngagementReplyProcessing'
